import { useState } from 'react';
import { useI18n } from '../i18n/i18n.jsx';
import Container from '../components/UI/Container';
import Section from '../components/UI/Section';
import SectionTitle from '../components/UI/SectionTitle';
import FormInput from '../components/UI/FormInput';
import { emailConfig } from '../config/email.js';

const Contact = () => {
  const { t } = useI18n();
  
  const [contactData, setContactData] = useState({
    subject: 'general',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    message: ''
  });
  
  const [validationErrors, setValidationErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [responseStatus, setResponseStatus] = useState(null); // 'success' | 'error' | null

  const handleChange = (e) => {
    const { name, value } = e.target;
    setContactData(prev => ({ ...prev, [name]: value }));
    
    // Clear error when user starts typing
    if (validationErrors[name]) {
      setValidationErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!contactData.firstName.trim()) {
      newErrors.firstName = t('contact.form.errors.firstName');
    }
    
    if (!contactData.lastName.trim()) {
      newErrors.lastName = t('contact.form.errors.lastName');
    }
    
    if (!contactData.email.trim()) {
      newErrors.email = t('contact.form.errors.emailRequired');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactData.email)) {
      newErrors.email = t('contact.form.errors.emailInvalid');
    }
    
    if (!contactData.message.trim()) {
      newErrors.message = t('contact.form.errors.message');
    }
    
    setValidationErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsLoading(true);
    setResponseStatus(null);
    
    try {
      // API'ye POST isteği gönder
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          subject: t(`contact.form.subjects.${contactData.subject}`),
          firstName: contactData.firstName,
          lastName: contactData.lastName,
          email: contactData.email,
          phone: contactData.phone,
          message: contactData.message
        }),
      });
      
      const result = await response.json();
      
      if (result.success) {
        // Başarılı
        setIsLoading(false);
        setResponseStatus('success');
        setContactData({ 
          subject: 'general', 
          firstName: '', 
          lastName: '', 
          email: '', 
          phone: '', 
          message: '' 
        });
        
        // Reset success message after 5 seconds
        setTimeout(() => setResponseStatus(null), 5000);
      } else {
        // Hata
        setIsLoading(false);
        setResponseStatus('error');
        console.error('API Error:', result.message);
        
        // Reset error message after 5 seconds
        setTimeout(() => setResponseStatus(null), 5000);
      }
      
    } catch (error) {
      console.error('Connection error:', error);
      setIsLoading(false);
      setResponseStatus('error');
      
      // Reset error message after 5 seconds
      setTimeout(() => setResponseStatus(null), 5000);
    }
  };

  const contactInfo = [
    {
      icon: '📞',
      key: 'phone',
      value: '+993 62393133',
      href: 'tel:+99362393133'
    },
    {
      icon: '✉️',
      key: 'email',
      value: emailConfig.toEmail,
      href: `mailto:${emailConfig.toEmail}`
    },
    {
      icon: '💬',
      key: 'whatsapp',
      value: '+993 61351020',
      href: 'https://wa.me/99361351020'
    }
  ];

  return (
    <main>
      {/* Hero Section */}
      <Section className="pt-20 sm:pt-28 pb-12 bg-neutral-light dark:bg-neutral-900/50">
        <Container>
          <div className="max-w-4xl mx-auto text-center animate-fade-in">
            <div className="w-12 md:w-16 h-1 bg-accent mx-auto mb-4 md:mb-6 rounded-full" />
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-serif font-semibold text-primary dark:text-white mb-3 md:mb-4">
              {t('contact.title')}
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-neutral-600 dark:text-neutral-300 leading-relaxed px-4">
              {t('contact.subtitle')}
            </p>
          </div>
        </Container>
      </Section>

      {/* Contact Form & Info */}
      <Section>
        <Container>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12">
            {/* Contact Form */}
            <div className="order-2 lg:order-1">
              <div className="bg-white dark:bg-neutral-800 p-6 md:p-8 rounded-2xl border border-neutral-200 dark:border-neutral-700 shadow-lg">
                <h2 className="text-xl md:text-2xl font-semibold text-primary dark:text-white mb-4 md:mb-6">
                  {t('contact.formTitle')}
                </h2>
                
                <form onSubmit={handleSubmit} className="space-y-4 md:space-y-6">
                  {/* Subject Dropdown */}
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                      {t('contact.form.subject')}
                    </label>
                    <select
                      name="subject"
                      value={contactData.subject}
                      onChange={handleChange}
                      className="w-full px-3 py-2 md:px-4 md:py-3 bg-white dark:bg-neutral-700 border border-neutral-300 dark:border-neutral-600 rounded-lg text-neutral-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200 text-sm md:text-base"
                    >
                      <option value="general">{t('contact.form.subjects.general')}</option>
                      <option value="sales">{t('contact.form.subjects.sales')}</option>
                      <option value="support">{t('contact.form.subjects.support')}</option>
                      <option value="partnership">{t('contact.form.subjects.partnership')}</option>
                      <option value="other">{t('contact.form.subjects.other')}</option>
                    </select>
                  </div>

                  <div className="grid grid-cols-2 gap-3 md:gap-4">
                    <FormInput
                      label={t('contact.form.firstName')}
                      name="firstName"
                      value={contactData.firstName}
                      onChange={handleChange}
                      placeholder={t('contact.form.firstNamePlaceholder')}
                      required
                      error={validationErrors.firstName}
                    />
                    
                    <FormInput
                      label={t('contact.form.lastName')}
                      name="lastName"
                      value={contactData.lastName}
                      onChange={handleChange}
                      placeholder={t('contact.form.lastNamePlaceholder')}
                      required
                      error={validationErrors.lastName}
                    />
                  </div>
                  
                  <FormInput
                    label={t('contact.form.email')}
                    type="email"
                    name="email"
                    value={contactData.email}
                    onChange={handleChange}
                    placeholder={t('contact.form.emailPlaceholder')}
                    required
                    error={validationErrors.email}
                  />
                  
                  <FormInput
                    label={t('contact.form.phone')}
                    type="tel"
                    name="phone"
                    value={contactData.phone}
                    onChange={handleChange}
                    placeholder={t('contact.form.phonePlaceholder')}
                  />
                  
                  <FormInput
                    label={t('contact.form.message')}
                    as="textarea"
                    name="message"
                    value={contactData.message}
                    onChange={handleChange}
                    placeholder={t('contact.form.messagePlaceholder')}
                    required
                    error={validationErrors.message}
                    rows={4}
                  />
                  
                  <button
                    type="submit"
                    disabled={isLoading}
                    className={`group relative w-full py-3 md:py-4 bg-primary text-white font-semibold rounded-xl overflow-hidden hover-lift shine-on-hover transition-all duration-300 text-sm md:text-base ${
                      isLoading
                        ? 'opacity-70 cursor-not-allowed'
                        : 'shadow-xl'
                    }`}
                  >
                    <span className="relative z-10">
                      {isLoading ? t('contact.form.sending') : t('contact.form.submit')}
                    </span>
                    {!isLoading && (
                      <div className="absolute inset-0 bg-primary-light opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    )}
                  </button>

                  {responseStatus === 'success' && (
                    <div className="p-3 md:p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-green-700 dark:text-green-300 text-sm md:text-base">
                      {t('contact.form.success')}
                    </div>
                  )}
                  
                  {responseStatus === 'error' && (
                    <div className="p-3 md:p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300 text-sm md:text-base">
                      {t('contact.form.error')}
                    </div>
                  )}
                </form>
              </div>
            </div>

            {/* Direct Contact Options */}
            <div className="order-1 lg:order-2">
              <div className="space-y-4 md:space-y-6">
                <div>
                  <h2 className="text-xl md:text-2xl font-semibold text-primary dark:text-white mb-1 md:mb-2">
                    {t('contact.direct.title')}
                  </h2>
                  <p className="text-sm md:text-base text-neutral-600 dark:text-neutral-300">
                    {t('contact.direct.subtitle')}
                  </p>
                </div>

                {contactInfo.map((item, index) => (
                  <a
                    key={index}
                    href={item.href}
                    className="group relative flex items-center gap-3 md:gap-4 p-4 md:p-6 bg-white dark:bg-neutral-800 rounded-xl border border-neutral-200 dark:border-neutral-700 hover-lift hover-border overflow-hidden shine-on-hover"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={t(`contact.direct.${item.key}`)}
                  >
                    <div className="text-2xl md:text-3xl">{item.icon}</div>
                    <div className="flex-1">
                      <div className="text-xs md:text-sm font-medium text-neutral-500 dark:text-neutral-400">
                        {t(`contact.direct.${item.key}`)}
                      </div>
                      <div className="text-sm md:text-lg font-semibold text-primary dark:text-white group-hover:text-accent transition-colors duration-200">
                        {item.value}
                      </div>
                    </div>
                    <svg
                      className="w-4 h-4 md:w-5 md:h-5 text-neutral-400 group-hover:text-accent transition-colors duration-200 transform group-hover:translate-x-1"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </a>
                ))}

                {/* Office Hours */}
                <div className="p-4 md:p-6 bg-primary/5 dark:bg-primary/10 rounded-xl border border-primary/20 dark:border-primary/30">
                  <h3 className="font-semibold text-primary dark:text-white mb-1 md:mb-2 text-sm md:text-base">
                    {t('contact.officeHours.title')}
                  </h3>
                  <p className="text-neutral-600 dark:text-neutral-300 text-xs md:text-sm leading-relaxed whitespace-pre-line">
                    {t('contact.officeHours.description')}
                  </p>
                </div>

                {/* Response Time */}
                <div className="p-4 md:p-6 bg-accent/5 dark:bg-accent/10 rounded-xl border border-accent/20 dark:border-accent/30">
                  <h3 className="font-semibold text-accent mb-1 md:mb-2 text-sm md:text-base">
                    {t('contact.responseTime.title')}
                  </h3>
                  <p className="text-neutral-600 dark:text-neutral-300 text-xs md:text-sm leading-relaxed">
                    {t('contact.responseTime.description')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </Section>

      {/* Final CTA */}
      <Section className="bg-primary text-white pb-24 md:pb-16">
        <Container>
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-serif font-semibold mb-3 md:mb-4">
              {t('contact.cta.title')}
            </h2>
            <p className="text-base sm:text-lg md:text-xl mb-6 md:mb-8 text-blue-100 px-4">
              {t('contact.cta.subtitle')}
            </p>
            <a
              href={`mailto:${emailConfig.toEmail}?subject=Free%20Consultation%20Request`}
              className="inline-block px-6 py-3 md:px-8 md:py-4 bg-white text-primary font-medium rounded-lg hover:bg-neutral-100 transition-colors duration-200 shadow-lg text-sm md:text-base"
            >
              {t('contact.cta.button')}
            </a>
          </div>
        </Container>
      </Section>
      {/* Bottom padding for mobile nav */}
      <div className="pb-24 md:pb-0"></div>
    </main>
  );
};

export default Contact;